import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  computed,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { BaseChartDirective } from 'ng2-charts';
import { ActiveElement, ChartData, ChartEvent, ChartOptions, ChartType, TooltipItem } from 'chart.js';
import { SpinnerComponent } from '../spinner/spinner.component';
import { ChartSyncService, SyncableChart } from './chart-sync.service';

const DST_MARKER_STYLE = {
  dstMarker: true,
  showLine: false,
  fill: false,
  pointStyle: 'triangle',
  pointRadius: 6,
  pointHoverRadius: 7,
};

export interface XemexChartDataset {
  label: string;
  data: (number | null)[];
  type: 'line' | 'bar';
  color: string;
  // Explicitly force an area fill on/off for this dataset. When omitted the chart's
  // `filled` input decides (primary-axis, non-dashed line datasets get filled).
  fill?: boolean;
  yAxisID?: string;
  borderRadius?: number;
  borderDash?: number[];
  // Datasets sharing a stack id are drawn on top of each other in the same slot.
  stack?: string;
  // Daylight saving marker: a point on the axis for an hour the clock skipped, its label is the tooltip.
  dstMarker?: boolean;
}

// Add an alpha channel to a hex or oklch/rgb/hsl colour string so it can be used
// as a translucent area fill while the solid colour stays on the line and legend.
// Exported for reuse by other Chart.js-backed components (e.g. `xemex-donut-chart`)
// that need the same hover-dim treatment.
export function withAlpha(color: string, alpha: number): string {
  const c = color.trim();
  if (c.startsWith('#')) {
    const hex = c.slice(1);
    const full = hex.length === 3 ? hex.replace(/./g, ch => ch + ch) : hex.slice(0, 6);
    return `#${full}${Math.round(alpha * 255)
      .toString(16)
      .padStart(2, '0')}`;
  }
  if (/^(oklch|oklab|rgb|hsl|lab|lch)\(/.test(c) && !c.includes('/')) {
    return c.replace(/\)\s*$/, ` / ${alpha})`);
  }
  return c;
}

@Component({
  selector: 'xemex-chart',
  standalone: true,
  host: { class: 'block' },
  imports: [BaseChartDirective, SpinnerComponent],
  templateUrl: './chart.component.html',
  styleUrl: './chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChartComponent implements SyncableChart, OnInit, OnDestroy {
  datasets = input.required<XemexChartDataset[]>();
  labels = input.required<string[]>();
  loading = input<boolean>(false);
  title = input<string>();
  // Tailwind height class for the plot area, e.g. `'h-64'` (default) or `'h-96'` for a
  // larger chart. Kept as a class rather than a numeric input so callers can use any
  // Tailwind height utility (including responsive variants) without extra plumbing here.
  heightClass = input<string>('h-64');
  // Small unit label (e.g. "kWh", "kW", "V", "A") rendered at the top-left of the plot.
  unit = input<string>();
  // Explanatory note rendered under the plot, e.g. how the repeated hour of a clock-change day is shown.
  note = input<string>();
  // Badge above the plot, e.g. naming the clock change of the day shown.
  badge = input<string>();
  // When true, line datasets on the primary axis get a translucent area fill.
  filled = input<boolean>(false);
  // Where a filled line's area is drawn: down to the baseline ('baseline'), or only the
  // portion below zero ('negative') so negative values are highlighted while positive
  // values stay line-only.
  fillMode = input<'baseline' | 'negative'>('baseline');
  options = input<ChartOptions | undefined>(undefined);
  chartType = input<'bar' | 'line'>('bar');
  // Opt out of an ancestor's hover-sync group. A shared index only identifies the same
  // moment when every chart in the group plots the same axis, so a chart on its own time
  // base (e.g. a 60-day trend beside charts covering a single day) must set this to false
  // or it will mirror an index that means something else entirely.
  syncHover = input<boolean>(true);

  // Present only when an ancestor (e.g. the insights page) provides the group; charts
  // rendered outside such a group inject null and keep their standalone hover behaviour.
  private readonly sync = inject(ChartSyncService, { optional: true });
  private readonly baseChart = viewChild(BaseChartDirective);

  // Registered here rather than in the constructor: `syncHover` is not readable until
  // Angular has set the inputs.
  ngOnInit(): void {
    if (this.syncHover()) this.sync?.register(this);
  }

  ngOnDestroy(): void {
    this.sync?.unregister(this);
  }

  chartData = computed<ChartData>(() => ({
    labels: this.labels(),
    datasets: this.datasets().map(ds => {
      const onPrimaryAxis = !ds.yAxisID || ds.yAxisID === 'y';
      const doFill =
        ds.fill ?? (this.filled() && ds.type === 'line' && onPrimaryAxis && !ds.borderDash);
      // Only the area below zero is shaded in 'negative' mode; above the origin the fill
      // is transparent so the line shows bare.
      const negativeOnly = doFill && this.fillMode() === 'negative';
      const fillColor = withAlpha(ds.color, 0.18);
      return {
        type: ds.type,
        label: ds.label,
        data: ds.data,
        backgroundColor: doFill && !negativeOnly ? fillColor : ds.color,
        borderColor: ds.color,
        fill: !doFill
          ? false
          : negativeOnly
            ? { target: 'origin', above: 'transparent', below: fillColor }
            : 'origin',
        ...(ds.yAxisID ? { yAxisID: ds.yAxisID } : {}),
        ...(ds.borderRadius !== undefined ? { borderRadius: ds.borderRadius } : {}),
        ...(ds.borderDash ? { borderDash: ds.borderDash } : {}),
        ...(ds.stack ? { stack: ds.stack } : {}),
        ...(ds.dstMarker ? DST_MARKER_STYLE : {}),
      };
    }),
  }));

  chartOptions = computed<ChartOptions>(() => {
    // Stack ids only take effect on stacked scales; datasets without a shared id stay side by side.
    const stacked = this.datasets().some(ds => !!ds.stack);
    const merged = deepMerge<ChartOptions>(
      {
        maintainAspectRatio: false,
        interaction: { mode: 'nearest', intersect: false, axis: 'xy' },
        // Reserve headroom so the top-left unit label clears the highest axis tick.
        layout: { padding: { top: 24 } },
        elements: {
          line: { tension: 0.4, borderWidth: 2 },
          point: { radius: 0, hitRadius: 12, hoverRadius: 4 },
        },
        scales: {
          x: { grid: { display: false }, border: { display: false }, stacked: stacked },
          y: { grid: { color: '#DFE3E7' }, border: { display: false }, stacked: stacked },
        },
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              usePointStyle: true,
              pointStyle: 'rectRounded',
              boxWidth: 8,
              boxHeight: 8,
              padding: 16,
              color: '#6B7280',
              font: { size: 12 },
              // Solid swatches (from the line colour) and hide unlabelled datasets such
              // as reference lines.
              generateLabels: (chart: any) =>
                (chart.data.datasets ?? [])
                  .map((ds: any, i: number) => ({ ds, i }))
                  .filter(({ ds }: any) => !!ds.label && !ds.dstMarker)
                  .map(({ ds, i }: any) => ({
                    text: ds.label,
                    fillStyle: ds.borderColor,
                    strokeStyle: ds.borderColor,
                    lineWidth: 0,
                    pointStyle: 'rectRounded',
                    hidden: !chart.isDatasetVisible(i),
                    datasetIndex: i,
                  })),
            },
          },
          tooltip: {
            callbacks: {
              // A daylight saving marker explains the skipped hour; its value means nothing.
              label: tooltipLabel,
            },
          },
        },
      },
      this.options() ?? {},
    );
    return this.sync && this.syncHover() ? this.applySyncOptions(merged) : merged;
  });

  // For charts in a sync group: force index-based hover so every chart resolves the
  // same interval, broadcast the hovered index to its siblings, and hide unlabelled
  // datasets (e.g. reference lines) from the tooltip. Layered on top of the merged
  // options so it wins over any per-chart interaction the caller passed in.
  private applySyncOptions(options: ChartOptions): ChartOptions {
    return deepMerge<ChartOptions>(options, {
      interaction: { mode: 'index', intersect: false, axis: 'x' },
      onHover: (_event: ChartEvent, elements: ActiveElement[]) => {
        this.sync?.broadcast(this, elements.length ? elements[0].index : null);
      },
      plugins: {
        tooltip: {
          mode: 'index',
          intersect: false,
          filter: (item: TooltipItem<'line' | 'bar'>) => !!item.dataset.label,
        },
      },
    });
  }

  // Called by the sync service on sibling charts: mirror the hovered interval by
  // activating every visible dataset that has a value at `index` (or clear when null).
  syncTooltipToIndex(index: number | null): void {
    const chart = this.baseChart()?.chart;
    if (!chart) return;

    const active =
      index == null
        ? []
        : chart.data.datasets
            .map((_dataset, datasetIndex) => ({ datasetIndex, index }))
            .filter(
              ({ datasetIndex }) =>
                chart.isDatasetVisible(datasetIndex) &&
                !!chart.data.datasets[datasetIndex].label &&
                chart.data.datasets[datasetIndex].data[index] != null,
            );

    chart.setActiveElements(active);
    if (chart.tooltip) {
      const point = active.length
        ? chart.getDatasetMeta(active[0].datasetIndex).data[index as number]
        : undefined;
      chart.tooltip.setActiveElements(active, { x: point?.x ?? 0, y: point?.y ?? 0 });
    }
    chart.update('none');
  }
}

function tooltipLabel(item: TooltipItem<ChartType>): string {
  const label = item.dataset.label ?? '';
  if ((item.dataset as { dstMarker?: boolean }).dstMarker) {
    return label;
  }
  return label ? `${label}: ${item.formattedValue}` : item.formattedValue;
}

function deepMerge<T extends object>(target: T, source: Partial<T>): T {
  const result: any = { ...target };
  for (const key of Object.keys(source) as (keyof T)[]) {
    const srcVal = (source as any)[key];
    const tgtVal = result[key];
    if (
      srcVal !== null &&
      srcVal !== undefined &&
      typeof srcVal === 'object' &&
      !Array.isArray(srcVal) &&
      tgtVal !== null &&
      tgtVal !== undefined &&
      typeof tgtVal === 'object' &&
      !Array.isArray(tgtVal)
    ) {
      result[key] = deepMerge(tgtVal, srcVal);
    } else if (srcVal !== undefined) {
      result[key] = srcVal;
    }
  }
  return result as T;
}
